import { useCallback, useMemo, type ReactNode } from 'react';
import { Auth0Provider, useAuth0, type Auth0ProviderOptions } from '@auth0/auth0-react';
import {
  ShellAuth0Context,
  type ShellAuth0ContextInterface,
  type ShellRedirectOptions,
} from './context';

/**
 * Renders the real `<Auth0Provider>` from `@auth0/auth0-react`, then an inner
 * adapter that calls `useAuth0()` and feeds the values into our own
 * `ShellAuth0Context`. This way `useShellAuth0()` in consumer code reads from
 * a single context regardless of whether the surrounding provider is
 * standalone or shell mode.
 */
export function StandaloneAuth0Provider({ children, ...auth0Options }: Auth0ProviderOptions) {
  return (
    <Auth0Provider {...auth0Options}>
      <StandaloneAdapter>{children}</StandaloneAdapter>
    </Auth0Provider>
  );
}

function StandaloneAdapter({ children }: { children: ReactNode }) {
  const {
    isAuthenticated,
    isLoading,
    user,
    error,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect,
    logout,
  } = useAuth0();

  // Wrap the SDK's variadic loginWithRedirect/logout into our parameter-free
  // shape. Memoized so the context value's identity is stable across renders.
  //
  // Captures the current pathname+search at call time and passes it as
  // appState.returnTo so the consuming app's onRedirectCallback can restore
  // the deep-link URL after the Auth0 login round-trip completes.
  // Consumers can override returnTo via options if needed.
  const wrappedLoginWithRedirect = useCallback(
    (options?: ShellRedirectOptions): Promise<void> => {
      return loginWithRedirect({
        appState: {
          returnTo: options?.returnTo ?? window.location.pathname + window.location.search,
        },
      });
    },
    [loginWithRedirect]
  );

  const wrappedLogout = useCallback(
    async (options?: ShellRedirectOptions): Promise<void> => {
      await logout({ logoutParams: { returnTo: options?.returnTo ?? window.location.origin } });
    },
    [logout]
  );

  const value = useMemo<ShellAuth0ContextInterface>(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      error,
      getAccessTokenSilently,
      getIdTokenClaims,
      loginWithRedirect: wrappedLoginWithRedirect,
      logout: wrappedLogout,
    }),
    [
      isAuthenticated,
      isLoading,
      user,
      error,
      getAccessTokenSilently,
      getIdTokenClaims,
      wrappedLoginWithRedirect,
      wrappedLogout,
    ]
  );

  return <ShellAuth0Context.Provider value={value}>{children}</ShellAuth0Context.Provider>;
}
